import { useEffect, useState } from 'react';
import { addArea, Area, Employee } from '../api';
import { api } from '../api/client';
import Modal from '../components/common/Modal';
import '../components/common/Modal.css';
import Spinner from '../components/common/Spinner';
import './Page.css';

const Areas = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    api.get<Area[]>('/admin/areas')
      .then(setAreas)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await addArea({ name });
      setShowModal(false);
      setName('');
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to add area');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = areas.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Areas</h1>
          <p className="page-subtitle">{areas.length} company areas</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Add Area
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="search-input"
          placeholder="Search areas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Area Name</th>
                <th>Assigned Employees</th>
                <th>Employee Count</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={3} className="empty-cell">No areas found</td></tr>
              ) : (
                filtered.map((area) => (
                  <tr key={area._id}>
                    <td><strong>{area.name}</strong></td>
                    <td>
                      <div className="areas-pill-list">
                        {((area.employees ?? []) as Employee[]).map((emp) =>
                          typeof emp === 'string' ? null : (
                            <span key={emp._id} className="area-tag">{emp.name}</span>
                          )
                        )}
                        {(area.employees ?? []).length === 0 && (
                          <span style={{ color: '#7A5C8A', fontSize: 13 }}>No employees</span>
                        )}
                      </div>
                    </td>
                    <td>{(area.employees ?? []).length}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Add Area" onClose={() => setShowModal(false)}>
          <form onSubmit={handleAdd}>
            {formError && <p className="form-error">{formError}</p>}
            <div className="form-field">
              <label>Area Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Packaging, QC Lab" required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add Area'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Areas;
